import { type CardTabListType } from 'antd/lib/card';
import { flatten, isEqual, omit, orderBy, sortBy, uniq } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { SchemaSelect } from 'components/damages/DamageSchema/SchemaSelect';
import { useDamageAttrs } from 'components/damages/withDamages';
import {
  confirmationStatusColumn,
  CREATED_DATE_RANGE_LIMIT,
  criticalColumn,
  damageIdColumn,
  getAssetColumn,
  getAssetStatusColumn,
  getInspectionsColumn,
  getObservationsColumn,
  getPrimaryGroupSourceObservationCreatedColumn,
  getPropagationsColumn,
  siteColumn,
  tasksColumn,
} from 'components/DamageTable2/columns';
import {
  type PresetFilterType,
  DataTableContext,
  DataTableContextProvider,
  DataTableWithoutContext,
} from 'components/DataTable';
import { type TQueryFilter, EFilterSortClearType } from 'components/DataTable/types';
import { getMultiFilterValueForKey, tableStorage } from 'components/DataTable/utils';
import { columnPickerStorage } from 'components/DataTable/Views/ColumnPicker/utils';
import { HORIZON_DAMAGE_TABLE_STORAGE_KEY } from 'components/DataTable/Views/helpers/tableStorageKeys';
import { multiSorterStorage } from 'components/DataTable/Views/MultiSorter/utils';
import { ExportReport } from 'components/ExportReport';
import { Tooltip } from 'components/ui';
import {
  BladeDamagePlotContainer,
  hasSingleValidBladeMakeModel,
} from 'horizon/components/Damages/BladeDamagePlot';
import { DamageComparer } from 'horizon/components/Damages/CompareOTron';
import { DamagesBulkEdit } from 'horizon/components/Damages/DamagesBulkEdit';
import { DamageSearch } from 'horizon/components/Damages/DamageSearch';
import {
  type MergeConfirmationState,
  MergeDamagesModal,
} from 'horizon/components/Damages/MergeDamagesModal';
import { MultiInspectionDamageIngestion } from 'horizon/components/Damages/MultiInspectionDamageIngestion/MultiInspectionDamageIngestion';
import { CreateTasksFromDamagesModal } from 'horizon/routes/Inspections/DamageList/CreateTasksFromDamagesModal';
import { generateDamageTableFilters } from 'horizon/routes/Inspections/DamageList/utils';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAssetType,
  type AtlasGqlBlade,
  type AtlasGqlGetHorizonDamagesQuery,
  type AtlasGqlHorizonDamage,
  type AtlasGqlTableView,
  AtlasGqlConfirmationStatus,
  AtlasGqlReportContext,
  AtlasGqlViewType,
  GetHorizonDamagesDocument,
  useAvailableComponentStatusesQuery,
  useGetBladeShapeQuery,
  useGetBladeWithShapeQuery,
  useSiteBladeMakeModelsQuery,
  useTurbineBladeMakeModelsQuery,
} from 'types/atlas-graphql';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { SKYSPECS_ORG } from 'utils/constants';
import { isProd, isProdReview } from 'utils/env';
import { useFeatures } from 'utils/features';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';
import {
  BLADE_CARDS,
  DAMAGE_CREATED_DATE_COLUMN,
  MULTI_TURBINE_BULK_UPLOADS,
  NEW_IMAGE_VIEWER,
} from 'utils/release-toggles';
import { SelectableAction, useSelectableRecords } from 'utils/selectable';
import {
  getItem as getLocalStorageItem,
  removeItem as removeLocalStorageItem,
  setItem as setLocalStorageItem,
} from 'utils/storage';
import { RenderWithAccess, usePermissions } from 'utils/usePermissions';

import { FilterControlsContainer, StyledAlert } from './DamageTable2.style';
import { DamageList2ExpandedRow } from './ExpandedRow';
import { type TDataQueryChangeArgs } from './types';

type DamageTable2Props = {
  id: string;
  title: string;
  defaultActiveTabKey?: string;
  damageIds?: string[];
  assetIds?: string[];
  assetTypes?: AtlasGqlAssetType[];
  critical?: boolean;
  controls?: boolean;
  savedViews?: boolean;
  externalLoading?: boolean;
};

const BaseDamageTable2: React.FunctionComponent<DamageTable2Props> = ({
  id,
  title,
  defaultActiveTabKey = 'ALL',
  damageIds,
  assetIds,
  assetTypes,
  critical = false,
  controls = true,
  savedViews = true,
  externalLoading = false,
}) => {
  const [filterBy, setFilterBy] = useState<TQueryFilter[]>([]);
  const [totalCount, setTotalCount] = useState<number>();
  const rowKeys = useRef<string[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  const [selectedSchema, setSelectedSchema] = useState<string>();
  const [damageComparerOpen, setDamageComparerOpen] = useState<boolean>(false);
  const [createTasksFromDamagesModalOpen, setCreateTasksFromDamagesModalOpen] = useState(false);
  const [damageSearchOpen, setDamageSearchOpen] = useState<boolean>(false);
  const [multiInspectionDamageIngestionModalOpen, setMultiInspectionDamageIngestionModalOpen] =
    useState<boolean>(false);
  const [mergeConfirmationState, setMergeConfirmationState] = useState<MergeConfirmationState>({
    isOpen: false,
  });
  const [mergedDamageId, setMergedDamageId] = useState<string>();
  // TODO: may want to put this as yet another thing in local storage, but leaving it here for now for development
  const [showPlot, setShowPlot] = useState<boolean>(false);
  const [bannerMessage, setBannerMessage] = useState<string>();

  const history = useHistory();
  const { hasRole } = usePermissions();
  const isEditor = hasRole('role-editor');

  const {
    DAMAGE_CRITICAL,
    DAMAGE_PLOT: hasDamagePlotFeatureFlag,
    BLADE_CARDS: hasBladeCardsFeatureFlag,
    DAMAGE_INGESTION: hasDamageIngestionFeatureFlag,
  } = useFeatures().features;

  const { customer, user, hasReleaseToggle } = useAccountContext();
  const hasDamageCreatedDateReleaseToggle = hasReleaseToggle(DAMAGE_CREATED_DATE_COLUMN);
  const hasNewImageViewerReleaseToggle = hasReleaseToggle(NEW_IMAGE_VIEWER);
  const hasBladeCardsReleaseToggle = hasReleaseToggle(BLADE_CARDS);
  const hasMultiTurbineBulkUploads = hasReleaseToggle(MULTI_TURBINE_BULK_UPLOADS);
  const hasUnplannedTaskSchemaSelectorToggle = hasReleaseToggle('unplanned-tasks-schema-selector');
  const isSiteDetailsPage = id === 'site-damage-list-table2';
  const isTurbineDetailsPage =
    assetTypes?.length === 1 && assetTypes?.[0]?.name === 'Turbine' && assetIds?.length === 1;
  const isBladeDetailsPage =
    assetTypes?.length === 1 && assetTypes?.[0]?.name === 'Blade' && assetIds?.length === 1;

  const tableViewStorageManager = tableStorage({ storageKey: `${id}_VIEW`, user });
  const columnPickerStorageManager = columnPickerStorage({
    storageKey: HORIZON_DAMAGE_TABLE_STORAGE_KEY.COLUMN_PICKER,
  });
  const multiSorterStorageManager = multiSorterStorage({
    storageKey: HORIZON_DAMAGE_TABLE_STORAGE_KEY.MULTI_SORT,
  });

  const {
    pagination,
    filteredInfo,
    updateFilteredInfo,
    sortedInfo,
    updateSortedInfo,
    setPersistedState,
    tableView,
    updateTableView,
    updateCustomizedColumnSet,
    updateMultiSortDef,
  } = useContext(DataTableContext);

  useEffect(() => {
    const { site, asset } = filteredInfo ?? {};

    let updatedSiteFilter: string[] | null | undefined = site;
    let updatedAssetFilter:
      | { key: string; value: (number | number[] | string)[] | string }[]
      | null = asset;

    // On the site details page, ensure the correct site id is in filters (prevents issues when switching between site pages)
    if (isSiteDetailsPage && !isEqual(filteredInfo?.site, assetIds)) {
      updatedSiteFilter = assetIds;
    }

    // If a previously filtered site is removed (including when the user changes between site details pages), remove any assets/components on that site
    if (asset?.some(({ key }) => ['assetId', 'componentId'].includes(key))) {
      const assetIds = asset
        ?.find(({ key }) => key === 'assetId')
        ?.value?.filter((v: string) => updatedSiteFilter?.includes(v.split(':')[1]));
      const componentIds = asset
        ?.find(({ key }) => key === 'componentId')
        ?.value?.filter((v: string) => updatedSiteFilter?.includes(v.split(':')[1]));

      updatedAssetFilter = [
        ...asset?.filter(({ key }) => !['assetId', 'componentId'].includes(key)),
        assetIds?.length ? { key: 'assetId', value: assetIds } : undefined,
        componentIds?.length ? { key: 'componentId', value: componentIds } : undefined,
      ].filter(Boolean);
    }

    if (!isEqual(site, updatedSiteFilter) || !isEqual(asset, updatedAssetFilter)) {
      updateFilteredInfo({
        ...filteredInfo,
        site: updatedSiteFilter,
        asset: updatedAssetFilter,
      });
    }
  }, [isSiteDetailsPage, assetIds, filteredInfo?.site]); // Intentionally omitting filteredInfo/updateFilteredInfo so this happens only once per time site changes

  useEffect(() => {
    const localStorageKey = 'observationCreatedAutoFilterApplied';
    const observationCreatedColumnKey = 'primaryGroupSourceObservationCreated';
    const currentObservationCreatedFilter = filteredInfo?.[observationCreatedColumnKey];
    const hasExistingAutoFilter = getLocalStorageItem(localStorageKey) === 'true';

    if (sortedInfo?.columnKey === observationCreatedColumnKey) {
      const hasShortWindowFilter = currentObservationCreatedFilter?.some((filter: string[]) => {
        if (!Array.isArray(filter) || filter.length < 2) {
          return false;
        }
        const start = moment(filter[0]);
        const end = moment(filter[1]);
        return end.diff(start, 'days') <= CREATED_DATE_RANGE_LIMIT;
      });

      if (!hasShortWindowFilter) {
        const lastNDaysFilter = [
          moment().subtract(CREATED_DATE_RANGE_LIMIT, 'days').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
        ];
        updateFilteredInfo({
          ...filteredInfo,
          [observationCreatedColumnKey]: [lastNDaysFilter],
        });
        setLocalStorageItem(localStorageKey, 'true');
      }

      /**
       * Inform the user of the auto-filter, whether applied from a manual sort here (!hasShortWindowFilter)
       * or from table state in local storage or a saved view.
       */
      if (!hasShortWindowFilter || hasExistingAutoFilter) {
        setBannerMessage(
          `To support sorting by Created Date, a filter for the past ${CREATED_DATE_RANGE_LIMIT} days has been automatically applied. This range may be adjusted, but must be a window of at most ${CREATED_DATE_RANGE_LIMIT} days.`
        );
      }
    } else if (currentObservationCreatedFilter && hasExistingAutoFilter) {
      updateFilteredInfo({ ...filteredInfo, [observationCreatedColumnKey]: null });
      removeLocalStorageItem(localStorageKey);
    }

    if (sortedInfo?.columnKey !== observationCreatedColumnKey) {
      setBannerMessage(undefined);
    }
  }, [sortedInfo, filteredInfo?.primaryGroupSourceObservationCreated]);

  const LINK_DAMAGES = { min: 2, max: 20 };
  const CREATE_TASKS = { min: 1, max: 200 };
  const {
    selected = [],
    rowSelection,
    onClearSelection,
  } = useSelectableRecords<AtlasGqlHorizonDamage>();

  const { damageColumns, loading: loadingAttrs } = useDamageAttrs(selectedSchema, true, critical);

  const { data: componentStatusData } = useAvailableComponentStatusesQuery();

  const { data: siteBladeMakeModelsData, loading: siteBladeDataLoading } =
    useSiteBladeMakeModelsQuery({
      variables: {
        siteId: assetIds?.[0] ?? '',
      },
      skip: !isSiteDetailsPage,
    });
  const { data: turbineBladeMakeModelsData, loading: turbineBladeDataLoading } =
    useTurbineBladeMakeModelsQuery({
      variables: {
        turbineId: assetIds?.[0] ?? '',
      },
      skip: !isTurbineDetailsPage,
    });

  // Plot setup logic
  const {
    plotMake,
    plotModel,
    plotAssetId,
  }: { plotMake?: string; plotModel?: string; plotAssetId?: string } = useMemo(() => {
    /**
     * Set plotMake/plotModel when on a site or turbine details page where all
     * blade descendants are of a matching make/model valid for plotting.
     *
     * This means that plotro may be enabled on details pages where there are
     * descendant assets of other types, as long as all of the blades are the same.
     * In these cases, the plot's make/model are set separately from filters.
     */
    if (
      isSiteDetailsPage &&
      hasSingleValidBladeMakeModel(siteBladeMakeModelsData?.siteBladeMakeModels)
    ) {
      const { make: plotMake, model: plotModel } = siteBladeMakeModelsData!.siteBladeMakeModels![0];
      return {
        plotMake,
        plotModel,
        plotAssetId: undefined,
      };
    }
    if (
      isTurbineDetailsPage &&
      hasSingleValidBladeMakeModel(turbineBladeMakeModelsData?.turbineBladeMakeModels)
    ) {
      const { make: plotMake, model: plotModel } =
        turbineBladeMakeModelsData!.turbineBladeMakeModels![0];
      return {
        plotMake,
        plotModel,
        plotAssetId: undefined,
      };
    }

    const { asset: assetFilters } = filteredInfo ?? {};
    const [
      assetType,
      assetMake,
      assetModel,
      componentType,
      componentMake,
      componentModel,
      assetId,
      componentId,
    ] = [
      'assetType',
      'assetMake',
      'assetModel',
      'componentType',
      'componentMake',
      'componentModel',
      'assetId',
      'componentId',
    ].map(k => getMultiFilterValueForKey(assetFilters ?? [], k));

    const plotMake =
      (isEqual(assetType, ['blade']) ? assetMake?.[0] : undefined) ??
      (isEqual(componentType, ['blade']) ? componentMake?.[0] : undefined) ??
      undefined;
    const plotModel =
      (isEqual(assetType, ['blade']) ? assetModel?.[0] : undefined) ??
      (isEqual(componentType, ['blade']) ? componentModel?.[0] : undefined) ??
      undefined;
    const plotAssetId =
      (isEqual(assetType, ['blade']) ? assetId?.[0]?.split(':')[0] : undefined) ??
      (isEqual(componentType, ['blade']) ? componentId?.[0]?.split(':')[0] : undefined) ??
      (assetTypes?.[0]?.name === 'Blade' && assetIds?.length === 1 ? assetIds[0] : undefined) ??
      undefined;

    return {
      plotMake,
      plotModel,
      plotAssetId,
    };
  }, [filteredInfo, assetTypes, assetIds, siteBladeMakeModelsData, turbineBladeMakeModelsData]);

  const { data: bladeShapeData } = useGetBladeShapeQuery({
    variables: { input: { make: plotMake!, model: plotModel! }, excludeWidths: true },
    skip: !plotMake && !plotModel,
  });
  const { data: assetBladeShapeData } = useGetBladeWithShapeQuery({
    variables: { id: plotAssetId!, excludeWidths: true },
    skip: !plotAssetId || (!!plotMake && !!plotModel),
  });

  const { plotEnabled, hasPlotFilters }: { plotEnabled: boolean; hasPlotFilters?: boolean } =
    useMemo(() => {
      if (!hasDamagePlotFeatureFlag) {
        return { plotEnabled: false };
      }

      const { asset: assetFilters } = filteredInfo ?? {};
      const [
        assetType,
        assetMake,
        assetModel,
        componentType,
        componentMake,
        componentModel,
        assetId,
        componentId,
      ] = [
        'assetType',
        'assetMake',
        'assetModel',
        'componentType',
        'componentMake',
        'componentModel',
        'assetId',
        'componentId',
      ].map(k => getMultiFilterValueForKey(assetFilters ?? [], k));

      const hasPlotFilters =
        (isEqual(assetType, ['blade']) &&
          ((assetMake?.length === 1 && assetModel?.length === 1) || assetId?.length === 1)) ||
        (isEqual(componentType, ['blade']) &&
          ((componentMake?.length === 1 && componentModel?.length === 1) ||
            componentId?.length === 1)) ||
        (assetTypes?.[0]?.name === 'Blade' && assetIds?.length === 1);
      const length =
        bladeShapeData?.makeModelData?.length ??
        (assetBladeShapeData?.assetWithMetadata as AtlasGqlBlade)?.bladeMakeModel?.length;

      const plotEnabled = !!(
        length &&
        (hasPlotFilters ||
          (isSiteDetailsPage &&
            hasSingleValidBladeMakeModel(siteBladeMakeModelsData?.siteBladeMakeModels)) ||
          (isTurbineDetailsPage &&
            hasSingleValidBladeMakeModel(turbineBladeMakeModelsData?.turbineBladeMakeModels)))
      );

      return {
        plotEnabled,
        hasPlotFilters,
      };
    }, [filteredInfo, bladeShapeData, assetBladeShapeData, assetTypes, assetIds]);

  useEffect(() => {
    if (showPlot && !plotEnabled) {
      setShowPlot(false);
    }
  }, [showPlot, plotEnabled]);

  useEffect(() => {
    /**
     * Open plot by default if on a blade details page with valid make/model
     * or site or turbine details page where all blade descendants are of a
     * matching valid make/model
     */
    if (
      plotEnabled &&
      (isSiteDetailsPage ||
        isTurbineDetailsPage ||
        isBladeDetailsPage ||
        tableView?.type === AtlasGqlViewType.BladeHotZone)
    ) {
      setShowPlot(true);
    }
  }, [plotEnabled, isSiteDetailsPage, isTurbineDetailsPage, isBladeDetailsPage, tableView]);

  // End plot setup

  const selectedDamagesHaveSameAsset = useMemo(() => {
    return selected.every(
      damage => !!damage.asset?.id && damage.asset.id === selected[0].asset?.id
    );
  }, [selected]);

  const allColumns: TTableColumnDef[] = useMemo(
    () => [
      ...[
        damageIdColumn,
        confirmationStatusColumn,
        tasksColumn,
        !isSiteDetailsPage && siteColumn,
        getAssetColumn(id, isSiteDetailsPage ? [] : assetTypes, isSiteDetailsPage ? [] : assetIds),
        getAssetStatusColumn(componentStatusData?.assetFieldDefinition?.fieldOptions ?? []),
        getInspectionsColumn(),
        getObservationsColumn(),
        getPropagationsColumn(),
      ].filter((c): c is TTableColumnDef => !!c),
      ...damageColumns,
      ...(hasDamageCreatedDateReleaseToggle
        ? [
            getPrimaryGroupSourceObservationCreatedColumn(
              sortedInfo?.columnKey === 'primaryGroupSourceObservationCreated'
            ),
          ]
        : []),
    ],
    [
      damageColumns,
      componentStatusData,
      isSiteDetailsPage,
      sortedInfo,
      hasDamageCreatedDateReleaseToggle,
    ]
  );

  const CONFIRMED_TAB_LIST: (CardTabListType & PresetFilterType)[] = useMemo(
    () => [
      {
        key: 'ALL',
        tab: 'All',
        value: {
          filters: {
            confirmationStatus: undefined,
            site: isSiteDetailsPage ? assetIds : undefined,
          },
          sortOrder: { columnKey: critical ? 'Date' : 'Severity', order: 'descend' },
        },
      },
      {
        key: 'CONFIRMED',
        tab: 'Confirmed',
        value: {
          filters: {
            confirmationStatus: [AtlasGqlConfirmationStatus.Confirmed],
            site: isSiteDetailsPage ? assetIds : undefined,
          },
        },
      },
      {
        key: 'UNCONFIRMED',
        tab: 'Unconfirmed',
        value: {
          filters: {
            confirmationStatus: [
              AtlasGqlConfirmationStatus.NotConfirmed,
              AtlasGqlConfirmationStatus.ConfirmedUpdated,
            ],
            site: isSiteDetailsPage ? assetIds : undefined,
          },
        },
      },
    ],
    [critical]
  );
  const initialPresetFilter = CONFIRMED_TAB_LIST.find(tab => tab.key === defaultActiveTabKey);

  // NOTE: these are necessary to synchronize the report export params with what is going on in the damage table
  const handleDataQueryChange = useCallback((args: TDataQueryChangeArgs) => {
    const { filterBy = [], totalCount = 0 } = args;
    setFilterBy(oldFilterBy => {
      if (isEqual(oldFilterBy, filterBy)) {
        return oldFilterBy;
      }
      setAllExpanded(false);
      setExpandedRowKeys([]);

      return filterBy;
    });
    setTotalCount(totalCount);
  }, []);

  const universalFilters: TQueryFilter[] = useMemo(
    () =>
      generateDamageTableFilters({
        schemaId: selectedSchema,
        assetIds: isSiteDetailsPage ? undefined : assetIds,
        siteIds: isSiteDetailsPage ? assetIds : undefined,
        assetTypes: isSiteDetailsPage ? undefined : assetTypes,
        damageIds,
        critical,
      }),
    [selectedSchema, assetIds, assetTypes, damageIds, critical, isSiteDetailsPage]
  );

  const transformQueryResponse = useCallback((data: AtlasGqlGetHorizonDamagesQuery) => {
    if (!data.getHorizonDamages) {
      return { totalCount: 0, items: [] };
    }

    const totalCount = data.getHorizonDamages?.totalCount ?? 0;
    const items = (data.getHorizonDamages?.items ?? []).filter(Boolean) as AtlasGqlHorizonDamage[];

    rowKeys.current = items.map(({ id }: AtlasGqlHorizonDamage) => id);

    return {
      totalCount,
      items: items.map(damage => ({
        ...damage,
        attrs: damage.primaryObservationGroupAttrs,
      })),
    };
  }, []);

  const createTasksForSelectedDamages = (selected: AtlasGqlHorizonDamage[]) => {
    if (selected.length) {
      setCreateTasksFromDamagesModalOpen(true);
    }
  };

  // logic for setting the state of the expand/collapse all button
  useEffect(() => {
    if (expandedRowKeys.length === 0) {
      setAllExpanded(false);
    } else if (rowKeys.current.length === expandedRowKeys.length) {
      setAllExpanded(true);
    }
  }, [rowKeys, expandedRowKeys]);

  // resets the table when the schema is changed
  const handleChangeSchema = (schema: string) => {
    if (!!selectedSchema && schema !== selectedSchema) {
      // reset customized columns
      updateCustomizedColumnSet(undefined);
      columnPickerStorageManager.remove();
      // reset multisort
      updateMultiSortDef(undefined);
      multiSorterStorageManager.remove();
      // reset table view
      updateTableView(undefined);
      tableViewStorageManager.remove();

      // reset filters/sorters to their default values
      const { filters = {}, sortOrder = {} } = initialPresetFilter?.value ?? {};
      updateFilteredInfo(filters);
      updateSortedInfo(sortOrder);
      setPersistedState({ filteredInfo: filters, sortedInfo: sortOrder, pagination });
    }
    setSelectedSchema(schema);
  };

  // handle updates to the saved view
  const handleSavedViewChange = (tableView: AtlasGqlTableView) => {
    const { schemaId } = tableView.customFields ?? {};

    schemaId && setSelectedSchema(schemaId);
  };

  // handler for expanding/collapsing rows
  const handleToggleExpandAll = () => {
    if (allExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(rowKeys.current);
    }
  };

  const handleMergeSelected = useCallback(() => {
    const allObservations = flatten(
      flatten(
        selected.map(damage => (damage.observationGroups ?? []).map(g => g?.observations ?? []))
      )
    );

    // Find which damage has the oldest inspection and merge into that one
    const oldestObservation = sortBy(allObservations, ['inspection.inspectionDate'])[0];
    if (!!oldestObservation) {
      const targetDamageId: string = oldestObservation.damage?.id;
      setMergeConfirmationState({
        isOpen: true,
        targetDamageId,
        sourceDamageIds: uniq(
          allObservations
            .filter(o => o?.damage?.id && o.damage.id !== targetDamageId)
            .map(o => o?.damage?.id)
        ),
      });
    }
  }, [selected]);

  // arguments for damage report
  const damageReportArgs = [
    {
      key: 'filterBy',
      value: JSON.stringify([
        ...(universalFilters || []),
        ...(filterBy || []),
        ...(selected?.length
          ? [
              {
                key: 'id',
                operator: 'EQUALS',
                values: selected.map(({ id }) => id),
              },
            ]
          : []),
      ]),
    },
  ];

  // for certain orgs, skip if there are no filters selected
  const skipQueryOrg: boolean = useMemo(() => {
    // check for the correct environment and org
    const isEnv = isProd || isProdReview;
    const isOrg = !!customer?.id && [SKYSPECS_ORG].includes(customer.id);

    // check for filters
    const hasUniversalFilters = universalFilters
      .map(filter => filter.key)
      .some(key => ['id', 'assetId', 'assetType', 'siteId', 'turbineId', 'critical'].includes(key));
    const hasFilters = filteredInfo && Object.values(filteredInfo).some(Boolean);

    return isEnv && isOrg && !hasUniversalFilters && !hasFilters;
  }, [customer, filteredInfo, universalFilters]);

  // wait until loading is done and schema is selected before querying
  // or for certan orgs with no filters selected
  const skipQuery = (controls && !selectedSchema) || skipQueryOrg;
  const loading = externalLoading || loadingAttrs || (skipQuery && !skipQueryOrg);

  return (
    <>
      {skipQueryOrg && (
        <StyledAlert
          type="info"
          showIcon={true}
          message={
            'Due to the large number of records in this table, please set a filter to view data.'
          }
        />
      )}
      {hasDamagePlotFeatureFlag && (
        <>
          <BladeDamagePlotContainer
            isOpen={showPlot}
            plotEnabled={plotEnabled}
            onToggle={() => {
              setShowPlot(showPlot => !showPlot);
            }}
            columns={allColumns}
            schemaId={selectedSchema}
            assetId={plotAssetId}
            make={plotMake}
            model={plotModel}
            turbineId={isTurbineDetailsPage ? assetIds?.[0] : undefined}
            isSiteDetailsPage={isSiteDetailsPage}
            isTurbineDetailsPage={isTurbineDetailsPage}
            isBladeDetailsPage={isBladeDetailsPage}
            criticalOnly={critical === true}
            assetBladeMakeModelDataLoading={
              (isSiteDetailsPage && (!siteBladeMakeModelsData || siteBladeDataLoading)) ||
              (isTurbineDetailsPage && (!turbineBladeMakeModelsData || turbineBladeDataLoading))
            }
            bladeSides={
              tableView?.type === AtlasGqlViewType.BladeHotZone
                ? tableView.filters?.['Blade Side']
                : undefined
            }
          />
        </>
      )}
      <DataTableWithoutContext
        id={id}
        title={title}
        loading={loading}
        dataQuery={GetHorizonDamagesDocument}
        onDataQueryChange={handleDataQueryChange}
        transform={transformQueryResponse}
        setTotalCount={setTotalCount}
        initialPresetFilter={initialPresetFilter}
        clearSortAndFilterType={EFilterSortClearType.RESET}
        routeProps={{
          universalFilter: universalFilters,
          variables: { input: {} },
          skip: skipQuery,
        }}
        deferCount={hasDamageCreatedDateReleaseToggle}
        columns={allColumns}
        bannerMessage={bannerMessage}
        isExpandAllVisible={true}
        isExpandAllEnabled={true}
        allExpanded={allExpanded}
        handleExpandAll={handleToggleExpandAll}
        expandable={{
          expandedRowRender: (damage: AtlasGqlHorizonDamage) => (
            <DamageList2ExpandedRow
              damage={damage}
              selectedSchema={selectedSchema}
              lazyLoad={true}
              mergedDamageId={mergedDamageId}
              setMergedDamageId={setMergedDamageId}
            />
          ),
          expandedRowKeys,
          onExpandedRowsChange: (expandedKeys: string[]) => {
            setExpandedRowKeys(expandedKeys);
          },
        }}
        showColumnPicker={true}
        showBladeCards={
          hasDamagePlotFeatureFlag && hasBladeCardsFeatureFlag && hasBladeCardsReleaseToggle
        }
        showSavedViews={savedViews}
        savedViewCustomFields={{ schemaId: selectedSchema }}
        onSavedViewChange={handleSavedViewChange}
        storageKey={HORIZON_DAMAGE_TABLE_STORAGE_KEY}
        controls={
          controls ? (
            <FilterControlsContainer
              hasUnplannedTaskSchemaSelectorToggle={hasUnplannedTaskSchemaSelectorToggle}
            >
              <SchemaSelect
                style={{ width: '200px' }}
                selected={selectedSchema}
                setSelected={handleChangeSchema}
                storageKey={`${id}-schema-select`}
              />
            </FilterControlsContainer>
          ) : undefined
        }
        rowSelection={rowSelection}
        onClearSelection={onClearSelection}
        actions={
          <>
            <FlexContainer>
              <SelectableAction selected={selected} onClear={onClearSelection} />
              {!critical && (
                <ExportReport
                  reportContexts={[
                    {
                      context: AtlasGqlReportContext.HorizonDamage,
                      reportArguments: [
                        {
                          args: damageReportArgs,
                          itemCount: selected?.length || totalCount,
                        },
                      ],
                    },
                  ]}
                  disabled={false}
                  cacheKey="damage-list-2"
                />
              )}
            </FlexContainer>
            {!!selected.length && (
              <SpacedContainer>
                <RenderWithAccess rules={[DAMAGE_EDIT_ATTRIBUTES]}>
                  <DamagesBulkEdit
                    selected={selected}
                    columns={[
                      DAMAGE_CRITICAL ? criticalColumn : undefined,
                      ...damageColumns,
                      confirmationStatusColumn,
                    ].filter(Boolean)}
                    onFinish={onClearSelection}
                  />
                </RenderWithAccess>
              </SpacedContainer>
            )}
          </>
        }
        additionalDropdownActions={[
          critical
            ? null
            : {
                content: 'Compare Selected',
                disabled: hasNewImageViewerReleaseToggle
                  ? selected.length !== 2
                  : selected.length < 2,
                onClick: () => {
                  if (hasNewImageViewerReleaseToggle) {
                    const group1 = selected[0].observationGroups?.find(
                      g => g?.isPrimaryObservationGroup
                    );
                    const group2 = selected[1].observationGroups?.find(
                      g => g?.isPrimaryObservationGroup
                    );
                    const pictureId1 = group1?.observations?.find(
                      o => o?.id === group1.sourceObservationId
                    )?.picture?.id;
                    const pictureId2 = group2?.observations?.find(
                      o => o?.id === group2.sourceObservationId
                    )?.picture?.id;

                    if (pictureId1 && pictureId2) {
                      history.push(
                        `/pictures/${pictureId1}?observation=${group1?.sourceObservationId}&picture2=${pictureId2}&observation2=${group2?.sourceObservationId}`,
                        { useModal: true }
                      );
                    }
                  } else {
                    setDamageComparerOpen(true);
                  }
                },
              },
          isEditor
            ? {
                content: (
                  <Tooltip
                    title={
                      selected.length < LINK_DAMAGES.min || selected.length > LINK_DAMAGES.max
                        ? `Select from ${LINK_DAMAGES.min} to ${LINK_DAMAGES.max} damages to link.`
                        : !selectedDamagesHaveSameAsset
                          ? 'Damages are assigned to different assets.'
                          : ''
                    }
                  >
                    Link Damages
                  </Tooltip>
                ),
                disabled:
                  !hasNewImageViewerReleaseToggle ||
                  selected.length < LINK_DAMAGES.min ||
                  selected.length > LINK_DAMAGES.max ||
                  !selectedDamagesHaveSameAsset,
                onClick: handleMergeSelected,
              }
            : null,
          hasDamagePlotFeatureFlag
            ? {
                content: (
                  <Tooltip
                    title={
                      plotEnabled
                        ? ''
                        : !hasPlotFilters
                          ? 'Filter by a single blade make/model or single blade to plot damages'
                          : 'Unable to plot due to missing length data'
                    }
                  >
                    Plot Damages
                  </Tooltip>
                ),
                disabled: !plotEnabled,
                onClick: () => setShowPlot(true),
              }
            : null,
          isEditor
            ? {
                content: (
                  <Tooltip
                    title={
                      selected.length < CREATE_TASKS.min || selected.length > CREATE_TASKS.max
                        ? `Select from ${CREATE_TASKS.min} to ${CREATE_TASKS.max} damages to create tasks for.`
                        : ''
                    }
                  >{`Create Task${selected.length > 1 ? 's' : ''}`}</Tooltip>
                ),
                disabled: selected.length < CREATE_TASKS.min || selected.length > CREATE_TASKS.max,
                onClick: () => createTasksForSelectedDamages(selected),
              }
            : null,
          {
            content: 'ID Search',
            onClick: () => setDamageSearchOpen(true),
          },
          hasDamageIngestionFeatureFlag && hasMultiTurbineBulkUploads
            ? {
                content: 'Upload Damage Data',
                onClick: () => setMultiInspectionDamageIngestionModalOpen(true),
              }
            : null,
        ].filter(Boolean)}
      />
      {!hasNewImageViewerReleaseToggle && selected.length > 1 && (
        <DamageComparer
          damageIds={orderBy(
            selected,
            [damage => damage.primaryObservationGroupAttrs?.Severity ?? 0],
            ['desc']
          ).map(({ id }) => id)}
          isOpen={damageComparerOpen}
          onClose={() => setDamageComparerOpen(false)}
        />
      )}
      <CreateTasksFromDamagesModal
        damages={selected}
        open={createTasksFromDamagesModalOpen}
        setOpen={setCreateTasksFromDamagesModalOpen}
      />
      <MergeDamagesModal
        mergeConfirmationState={mergeConfirmationState}
        onClose={() => {
          setMergeConfirmationState({ isOpen: false });
        }}
        onSuccess={damage => {
          // find selected rows that are no longer in the table
          // (i.e. the ones that were merged into the remaining damage)
          const selectedRows = selected.filter(
            ({ id }) => !mergeConfirmationState.sourceDamageIds?.includes(id)
          );

          // remove the now-merged rows from the table selection
          rowSelection.onChange(
            selectedRows.map(({ id }) => id),
            selectedRows
          );

          // set merged damage to update
          setMergedDamageId(damage.id);

          // for emphasis, deselect the remaining row after a delay
          setTimeout(() => {
            onClearSelection();
          }, 2500);
        }}
      />
      <DamageSearch isOpen={damageSearchOpen} onClose={() => setDamageSearchOpen(false)} />
      <MultiInspectionDamageIngestion
        isOpen={multiInspectionDamageIngestionModalOpen}
        setModalOpen={setMultiInspectionDamageIngestionModalOpen}
      />
    </>
  );
};

export const DamageTable2: React.FunctionComponent<DamageTable2Props> = props => {
  return (
    <DataTableContextProvider {...omit(props, ['controls'])}>
      <BaseDamageTable2 {...props} />
    </DataTableContextProvider>
  );
};
